/**
 * Landing v2 — gerçek kurs verisini pazarlama bileşenleri için sadeleştiren
 * küçük yardımcı fonksiyonlar. Hiçbir kurs adı/başlığı uydurulmaz; sadece
 * API'den gelen kayıtlar filtrelenir/sıralanır.
 *
 * NOT (karar gerekli): DB'de "course_test_*", "sim_*" id'li ya da başlığında
 * "(Test)" / "Test" / "Deneme" geçen demo-amaçlı kayıtlar var. Bunlar kalıcı
 * çözüm olarak backend'de bir `is_demo`/`visible_on_landing` alanıyla
 * işaretlenmeli; burada sadece geçici bir frontend sezgiseli uygulanıyor.
 */

export const CATEGORY_PRIORITY = {
  cabin_crew: 0,
  language: 1,
  business: 2,
  hr_training: 2,
  // landing-populer-ve-sayac: 'health' önceden bu haritada yoktu, `?? 9`
  // varsayılanına düşüyordu — sortByCategoryPriority ile hep en sona
  // atılıyordu. Prod'da EN KALABALIK kategori (17/32 kurs) health; kartlarda
  // hiç görünmemesi kategori çeşitliliği hatasının kök nedenlerinden biriydi.
  health: 3,
};

export function isDemoOrTestCourse(course) {
  if (!course) return true;
  const id = course.course_id || '';
  const title = `${course.title || ''} ${course.title_tr || ''}`.trim();
  if (id.startsWith('sim_')) return true;
  if (id.startsWith('course_test_')) return true;
  if (/\(test\)/i.test(title)) return true;
  if (/^test$/i.test((course.title || '').trim())) return true;
  if (/^test kursu$/i.test((course.title || '').trim())) return true;
  if (/^deneme$/i.test((course.title || '').trim())) return true;
  return false;
}

export function dedupeByTitle(courses) {
  const seen = new Set();
  const out = [];
  for (const c of courses) {
    const key = (c.title_tr || c.title || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

export function sortByCategoryPriority(courses) {
  return [...courses].sort((a, b) => {
    const pa = CATEGORY_PRIORITY[a.category] ?? 9;
    const pb = CATEGORY_PRIORITY[b.category] ?? 9;
    if (pa !== pb) return pa - pb;
    return (b.enrolled_count || 0) - (a.enrolled_count || 0);
  });
}

/**
 * landing-populer-ve-sayac — kategori ÇEŞİTLİLİĞİ için round-robin seçim.
 *
 * KÖK NEDEN: `sortByCategoryPriority` önce kategoriye göre sıralar; bu yüzden
 * TÜM `language` kursları TÜM `business`/`health` kurslarının önüne geçiyordu.
 * Prod'da 32 kurstan 17'si `health` — ".slice(0, N)" bu sıralamayla hiçbir
 * zaman `health` kursuna ulaşamıyordu (4 kartlık örnekte 3'ü aynı kategoriden
 * çıkıyor, dolayısıyla aynı kategori varsayılan görseli tekrarlanıyordu).
 *
 * `interleaveByCategory` bunun yerine önce her kategoriyi kendi içinde
 * (enrolled_count DESC, eşitlikte lessons_count DESC) sıralar, sonra
 * `CATEGORY_PRIORITY` sırasına göre dizilmiş kategori gruplarından DÖNÜŞÜMLÜ
 * (round-robin) kurs çeker: 1. tur her kategoriden en iyi 1 kurs, 2. tur her
 * kategoriden 2. kurs, vb. Bir kategori tükenirse diğerleri devam eder.
 */
export function interleaveByCategory(courses, limit) {
  const groups = new Map();
  for (const course of courses || []) {
    const key = course?.category ?? '__none__';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(course);
  }

  const orderedKeys = [...groups.keys()].sort((a, b) => {
    const pa = CATEGORY_PRIORITY[a] ?? 9;
    const pb = CATEGORY_PRIORITY[b] ?? 9;
    return pa - pb;
  });

  const sortedGroups = orderedKeys.map((key) => {
    const list = [...groups.get(key)];
    list.sort((a, b) => {
      const ea = a.enrolled_count || 0;
      const eb = b.enrolled_count || 0;
      if (eb !== ea) return eb - ea;
      return (b.lessons_count || 0) - (a.lessons_count || 0);
    });
    return list;
  });

  const result = [];
  let round = 0;
  while (result.length < limit) {
    let addedThisRound = false;
    for (const group of sortedGroups) {
      if (result.length >= limit) break;
      if (round < group.length) {
        result.push(group[round]);
        addedThisRound = true;
      }
    }
    if (!addedThisRound) break;
    round += 1;
  }

  return result;
}

export function cleanCourseList(courses) {
  return dedupeByTitle((courses || []).filter((c) => !isDemoOrTestCourse(c)));
}

/**
 * Kategori bazlı kart görseli eşlemesi. Kurs modelinde per-kurs `thumbnail`
 * alanı DB'de var ama (bugün itibarıyla) 32 kurstan 31'i boş — bu yüzden
 * kategori seviyesinde bir varsayılan görsel HAVUZU tutulur. Asset'ler
 * frontend/public/images/landing/ altında, telifsiz (Unsplash License).
 *
 * landing-populer-ve-sayac: kategori başına TEKİL görsel yerine HAVUZ.
 * Aynı kategoride birden fazla kart görünürse (örn. round-robin ile 2-3 tane
 * `language` kartı aynı ekranda çıkabilir) hepsi aynı fotoğrafı kullanmasın
 * diye deterministik olarak (bkz. `pickFromPool`) havuzdan seçim yapılır.
 * `health` kategorisi BİLİNÇLİ OLARAK burada YOK — 17/32 prod kursu bu
 * kategoride ve hiçbirinin konusuna uygun stok fotoğrafımız yok; PopularCourses
 * bu kategoride hiç `<img>` render etmez (gradyan + ikon), bkz. o dosyadaki not.
 */
export const CATEGORY_IMAGE_POOL = {
  cabin_crew: [
    '/images/landing/cabin-crew-1.webp',
    '/images/landing/cabin-interior-1.webp',
    '/images/landing/safety-1.webp',
  ],
  // fix(landing): `sign-language-1.webp` ve `aviation-english-1.webp` bu
  // havuzdan ÇIKARILDI. Bunlar KONU-SPESİFİK görseller (işaret dili elleri /
  // kokpit) — havuz hash'i (`pickFromPool`) rastgele dağıttığı için örn.
  // "Havacılık İngilizcesi" kartına işaret dili fotoğrafı düşebiliyordu.
  // İkisi de artık `COURSE_IMAGE_BY_RAW` üzerinden AÇIK eşleşmeyle atanıyor
  // (bkz. aşağıdaki harita) — havuzda kalmalarına gerek yok, kalırlarsa
  // yanlış-eşleşme riski yeniden doğar. `language-french-1.webp` (Eyfel) ve
  // `language-arabic-1.webp` genel "dil öğrenimi" çağrışımı taşıdığı için
  // havuzda kalabilir; hâlâ ilgisiz bir kursa (örn. gelecekte eklenecek yeni
  // bir dil kursu) düşseler bile en azından "dil" temasıyla çelişmezler.
  language: ['/images/landing/language-french-1.webp', '/images/landing/language-arabic-1.webp'],
  // NOT (düzeltme, landing-v2-duzeltmeler madde 6): 'business' önceden
  // interview-1.webp (mülakat/iş görüşmesi fotoğrafı) kullanıyordu — DB'deki
  // tek 'business' kategorili kurs (İnsan Kaynakları Eğitimi) için alakasız
  // bir eşlemeydi. training-1.webp (sınıf/eğitim ortamı) daha uygun; ayrıca
  // 'hr_training' kategorisiyle de görsel/anlam olarak tutarlı hale geldi.
  // landing-populer-ve-sayac: interview-1.webp atıl asset'ti (hiçbir kodda
  // kullanılmıyordu) — havuza eklenerek geri kazanıldı.
  business: ['/images/landing/training-1.webp', '/images/landing/interview-1.webp'],
  hr_training: ['/images/landing/training-1.webp', '/images/landing/interview-1.webp'],
  // Asagidaki iki eslesme, DB'de bu kategoride kurs varsa aircraft-1.webp
  // (genel havacilik gorseli, konuyla alakasiz) fallback'ine dusmesini
  // onlemek icin eklendi (bkz. Digital Marketing Strategy / marketing,
  // hero slayt 3 kart 1'de dinamik olarak gorunebiliyor).
  marketing: ['/images/landing/marketing-analytics-1.webp'],
  programming: [
    '/images/landing/programming-1.webp',
    '/images/landing/web-development-1.webp',
    '/images/landing/ai-literacy-1.webp',
  ],
  // landing-v3 İş 2: DB'de gerçek `design` kategorili kurs var (UI/UX Design
  // Bootcamp) — önceden bu haritada karşılığı yoktu, aircraft-1.webp
  // fallback'ine düşüyordu.
  design: ['/images/landing/ui-ux-design-1.webp'],
};

/**
 * Deterministik havuz seçimi — aynı `seed` (kurs `course_id`'si) HER ZAMAN
 * aynı elemanı döner (render'lar arası zıplama olmaz), farklı seed'ler
 * havuzda farklı elemanlara dağılır.
 */
export function pickFromPool(pool, seed) {
  if (!pool || pool.length === 0) return null;
  let hash = 0;
  for (const ch of String(seed ?? '')) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return pool[hash % pool.length];
}

/**
 * Kurs bazlı görsel override haritası (landing-v3, İş 2). Ham başlığa
 * (title_tr veya title_en/title) göre anahtarlanır — `COURSE_TITLE_ALIAS_BY_RAW`
 * ile aynı desen. Yalnızca kategori-seviyesi varsayılanın YETERSİZ kaldığı
 * durumlarda (aynı kategoride birden fazla, konu olarak farklı kurs) kullanılır:
 *
 *  - `programming` kategorisinde 3 farklı gerçek kurs var (Python / Full Stack
 *    Web / Yapay Zeka) — her biri kendi konusuna uygun görseli alır, aksi halde
 *    hepsi aynı genel "devre kartı" (programming-1.webp) görseline düşerdi.
 *  - `language` kategorisinde "İşaret Dili Eğitimi" ve "Havacılık İngilizcesi"
 *    (ham DB değeri "İş İngilizcesi"/"Is Ingilizcesi") kategori varsayılanı
 *    (language-french-1.webp, Eyfel Kulesi) ile HİÇBİR alakası olmayan bir
 *    görsele düşüyordu — ikisi de kendi konusuna özel görsel alır.
 *
 * fix(landing) — 17 satır PROD DB'deki gerçek ham başlıklarla eklendi (bkz.
 * PM raporu): önceki tur `CATEGORY_IMAGE_POOL.language` havuzunu 4 elemana
 * çıkarmıştı ama havuz hash'i KONU-SPESİFİK görselleri (işaret dili elleri /
 * kokpit) rastgele dağıtıyordu — "Havacılık İngilizcesi" kartına işaret dili
 * fotoğrafı, "İşaret Dili (TİD)" kartına kokpit fotoğrafı düşme riski vardı.
 * Bu satırlar prod'daki GERÇEK kurs başlıklarını (case/Türkçe karakter
 * varyasyonlarıyla birlikte, aşağıdaki `normalizeTitleKey` sayesinde) kendi
 * konusuna özel görsele sabitler; havuz artık sadece bilinmeyen/yeni kurslar
 * için güvenlik ağıdır (bkz. `getCourseImage` önce/sonra sırası).
 *
 * ÖNEMLİ — lookup TAM-STRING değil, NORMALİZE edilmiş anahtarla yapılır (bkz.
 * `normalizeTitleKey` + `COURSE_IMAGE_BY_RAW_NORMALIZED`). Önceki tam-string
 * eşleşme deseni `COURSE_TITLE_ALIAS_BY_RAW`'da Türkçe karakter bozukluğu
 * yüzünden sessizce kırılmıştı (dead code — bkz. o haritanın yorumu); aynı
 * tuzağa burada düşmemek için anahtarlar küçük harfe çevrilip Türkçe özel
 * karakterler sadeleştirilir ve noktalama atılır — "İşaret Dili (TİD)",
 * "isaret dili tid", "İŞARET DİLİ (TİD)" hepsi aynı normalize edilmiş
 * anahtara düşer.
 */
export const COURSE_IMAGE_BY_RAW = {
  'Yapay Zeka ve Makine Ogrenimi Temelleri': '/images/landing/ai-literacy-1.webp',
  'AI & Machine Learning Fundamentals': '/images/landing/ai-literacy-1.webp',
  'Full Stack Web Gelistirme': '/images/landing/web-development-1.webp',
  'Web Development Full Stack': '/images/landing/web-development-1.webp',
  'İşaret Dili Eğitimi': '/images/landing/sign-language-1.webp',
  'İş İngilizcesi': '/images/landing/aviation-english-1.webp',
  'Business English': '/images/landing/aviation-english-1.webp',
  'Is Ingilizcesi': '/images/landing/aviation-english-1.webp',
  'English for Business': '/images/landing/aviation-english-1.webp',
  // fix(landing) — prod DB'den birebir alınan gerçek ham başlıklar (17 satır).
  'Havacılık İngilizcesi': '/images/landing/aviation-english-1.webp',
  'Aviation English': '/images/landing/aviation-english-1.webp',
  'Hosteslik Eğitimi ve Havacılık İngilizcesi': '/images/landing/airport-1.webp',
  'İşaret Dili (TİD)': '/images/landing/sign-language-1.webp',
  'Turkish Sign Language (TİD)': '/images/landing/sign-language-1.webp',
  'Havacılıkta İşaret Dili': '/images/landing/sign-language-1.webp',
  // Eyfel Kulesi görseli BURADA doğru — bu gerçekten Fransızca dil kursu.
  'Fransızca Dil Eğitimi A1': '/images/landing/language-french-1.webp',
  'Diksiyon ve Etkili İletişim': '/images/landing/interview-1.webp',
  'Diction and Effective Communication': '/images/landing/interview-1.webp',
  'DİKSİYON ETKİLİ İLETİŞİM VE HİTABET': '/images/landing/interview-1.webp',
  'İnsan Kaynakları Yönetimi': '/images/landing/training-1.webp',
  'Human Resources Management': '/images/landing/training-1.webp',
  // fix(landing): 'business' havuzunda sadece 2 asset var (training-1 /
  // interview-1) — prod'daki KALAN business kursları da AÇIK override ile
  // sabitlenmedikçe hash (`pickFromPool`) bunları rastgele interview-1.webp'e
  // düşürebiliyordu; "Diksiyon ve Etkili İletişim" (yukarıda AÇIK override ile
  // interview-1.webp) ile aynı 10-kartlık ekranda GÖRÜNÜR tekrar oluşuyordu
  // (bkz. tester bulgusu — Kart 5 vs Kart 7, arada tek kart var). Aşağıdaki
  // 5 satır prod'daki kalan business başlıklarını sabitler.
  'Profesyonel Yaşam Koçluğu': '/images/landing/training-1.webp',
  'Professional Life Coaching': '/images/landing/training-1.webp',
  'Profesyonel Eğitim ve Öğrenci Koçluğu': '/images/landing/training-1.webp',
  'Professional Education and Student Coaching': '/images/landing/training-1.webp',
  'İleri Düzey İletişim Teknikleri': '/images/landing/interview-1.webp',
  // analitik/tablo görseli — muhasebe ile en uyumlu mevcut asset (özel bir
  // "muhasebe" fotoğrafımız yok, bkz. PM'in kısıtı — uydurma asset eklenmedi).
  'Mali Müşavir Yardımcılığı': '/images/landing/marketing-analytics-1.webp',
  'Genel Muhasebe Eğitimi': '/images/landing/marketing-analytics-1.webp',
  'Kalite Yönetim Sistemi Temel Eğitim - ISO 9001:2015': '/images/landing/training-1.webp',
  'Kabin Memurluğu': '/images/landing/cabin-crew-1.webp',
  'Cabin Crew': '/images/landing/cabin-crew-1.webp',
};

/**
 * Türkçe karakterleri ASCII köküne indirger, küçük harfe çevirir, noktalama/
 * fazladan boşlukları atar. `COURSE_IMAGE_BY_RAW` lookup'ının TAM-STRING
 * eşleşme yerine normalize edilmiş anahtarla yapılmasını sağlar — büyük/küçük
 * harf ve Türkçe karakter varyasyonları (İ/ı/ş/ğ/ü/ö/ç) aynı anahtara düşer.
 * `String.prototype.toLowerCase()` tek başına yeterli DEĞİL: 'İ' (U+0130)
 * lowercase'de 2 code point'lik "i̇" üretir (i + combining dot above) — bu
 * yüzden Türkçe karakterler standart lowercase'den ÖNCE, karakter bazında
 * ASCII karşılığına eşlenir.
 */
const TR_CHAR_TO_ASCII = {
  İ: 'i', I: 'i', ı: 'i',
  Ş: 's', ş: 's',
  Ğ: 'g', ğ: 'g',
  Ü: 'u', ü: 'u',
  Ö: 'o', ö: 'o',
  Ç: 'c', ç: 'c',
};

export function normalizeTitleKey(raw) {
  if (!raw) return '';
  let ascii = '';
  for (const ch of String(raw)) {
    ascii += TR_CHAR_TO_ASCII[ch] ?? ch;
  }
  return ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * `COURSE_IMAGE_BY_RAW`'ın build-time'da BİR KEZ normalize edilmiş hâli —
 * her `getCourseImage` çağrısında tüm haritayı gezmek yerine O(1) Map lookup.
 */
const COURSE_IMAGE_BY_RAW_NORMALIZED = new Map(
  Object.entries(COURSE_IMAGE_BY_RAW).map(([rawTitle, image]) => [normalizeTitleKey(rawTitle), image])
);

/** Kategori/kurs bazlı hiçbir eşleşme bulunamazsa kullanılan SON ÇARE görsel. */
export const DEFAULT_COURSE_IMAGE = '/images/landing/aircraft-1.webp';

/**
 * PopularCourses ve TrendingCourses'ın TEK ORTAK görsel seçim fonksiyonu
 * (landing-v3, İş 2 — kök neden: PopularCourses önceden `index %
 * POSTER_IMAGE_ROTATION.length` ile SIRAYA göre görsel seçiyordu, kursun
 * konusuyla hiçbir ilgisi yoktu). Öncelik sırası (landing-populer-ve-sayac ile
 * güncellendi):
 *   1) `course.thumbnail` — DB'de admin panelden girilmiş gerçek kurs görseli
 *      (dolu ve boş string değilse). Bugün 32 kurstan 31'i boş ama admin
 *      panelden thumbnail girildikçe landing kendiliğinden iyileşsin diye
 *      EN YÜKSEK öncelik burada.
 *   2) kurs bazlı override (`COURSE_IMAGE_BY_RAW`, `normalizeTitleKey` ile
 *      normalize edilmiş ham başlığa göre) — ÖNCE bu denenir, çünkü havuz
 *      (adım 3) konu-spesifik doğruluk GARANTİ ETMEZ.
 *   3) kategori görsel HAVUZU (`CATEGORY_IMAGE_POOL`), `course_id` seed'iyle
 *      deterministik seçim (bkz. `pickFromPool`) — SADECE yukarıdaki açık
 *      eşleşmenin karşılığı olmayan (bilinmeyen/yeni eklenen) kurslar için
 *      bir GÜVENLİK AĞIdır. Havuz seçimi rastgele (hash-tabanlı) dağıtım
 *      yaptığı için aynı kategorideki konu-spesifik iki kurs (örn. "işaret
 *      dili" / "havacılık İngilizcesi") birbirinin görselini alabilir — bu
 *      yüzden gerçek/bilinen kurslar için HER ZAMAN adım 2'deki açık
 *      eşleşme tercih edilmeli, havuz yalnızca son çareden bir önceki katman.
 *   4) son çare (`DEFAULT_COURSE_IMAGE`)
 */
export function getCourseImage(course) {
  if (!course) return DEFAULT_COURSE_IMAGE;
  if (typeof course.thumbnail === 'string' && course.thumbnail.trim() !== '') {
    return course.thumbnail;
  }
  const trRaw = course.title_tr || course.title || '';
  const enRaw = course.title_en || course.title || '';
  const override =
    COURSE_IMAGE_BY_RAW_NORMALIZED.get(normalizeTitleKey(trRaw)) ||
    COURSE_IMAGE_BY_RAW_NORMALIZED.get(normalizeTitleKey(enRaw));
  if (override) return override;
  const pool = CATEGORY_IMAGE_POOL[course.category];
  const pooled = pickFromPool(pool, course.course_id);
  return pooled || DEFAULT_COURSE_IMAGE;
}

/**
 * SUNUM KATMANI görünen-ad eşlemesi (madde 6) — DB'deki gerçek kurs kaydı/adı
 * DEĞİŞMEZ, sadece landing'de gösterilen başlık burada override edilir.
 * Eşleme hem TR hem EN ham alan değerine göre anahtarlanır (getField hangi
 * alanı seçerse seçsin doğru sonucu bulur).
 *
 * `group` alanı yalnızca sunum katmanının davranış kararları için kullanılır
 * (örn. `isAviationEnglishCourse` — bir kursun Havacılık sekmesine mi Diller
 * sekmesine mi ait olduğunu belirlemek); görünen metni ETKİLEMEZ.
 */
const COURSE_TITLE_ALIAS_BY_RAW = {
  'İş İngilizcesi': { tr: 'Havacılık İngilizcesi', en: 'Aviation English', group: 'aviation_english' },
  'Business English': { tr: 'Havacılık İngilizcesi', en: 'Aviation English', group: 'aviation_english' },
  // DÜZELTME (landing-v2-duzeltmeler madde 6): DB'deki GERÇEK ham değerler
  // yukarıdaki iki anahtarla eşleşmiyordu ('title_tr'/'title' alanlarında
  // fiilen "Is Ingilizcesi" / "English for Business" kayıtlı — Türkçe
  // karakter bozukluğu + farklı EN başlık). Bu yüzden alias önceden hiçbir
  // zaman eşleşmiyordu (dead code). Gerçek ham değerler eklendi.
  'Is Ingilizcesi': { tr: 'Havacılık İngilizcesi', en: 'Aviation English', group: 'aviation_english' },
  'English for Business': { tr: 'Havacılık İngilizcesi', en: 'Aviation English', group: 'aviation_english' },
  // "İnsan Kaynakları Egitimi" (DB'de kayıtlı ham değer) → doğru TR yazımı
  // "Eğitimi" (ğ eksikti). EN ham alan DB'de yok (title_en boş, title/title_tr
  // ile aynı ham değere düşüyor) — bu yüzden EN görünüm adı da burada verilir.
  'İnsan Kaynakları Egitimi': { tr: 'İnsan Kaynakları Eğitimi', en: 'Human Resources Training' },
  // landing-v3 İş 3: aşağıdaki 4 kurs DB'de seed script'i (`backend/routes/auth.py`
  // `/api/seed`) üzerinden oluşturulmuş gerçek kayıtlar — ham `title_tr` değerleri
  // Türkçe özel karakter eksik/bozuk (ğ/ı/ş yok), `title` (EN) alanı zaten temiz
  // olduğu için EN tarafı DEĞİŞTİRİLMEDEN aynen taşınır (yalnızca TR yazımı düzeltilir).
  'Python Programlama Ustaligi': { tr: 'Python Programlama Ustalığı', en: 'Python Programming Masterclass' },
  'Yapay Zeka ve Makine Ogrenimi Temelleri': { tr: 'Yapay Zeka ve Makine Öğrenimi Temelleri', en: 'AI & Machine Learning Fundamentals' },
  'Full Stack Web Gelistirme': { tr: 'Full Stack Web Geliştirme', en: 'Web Development Full Stack' },
  'UI/UX Tasarim Bootcamp': { tr: 'UI/UX Tasarım Bootcamp', en: 'UI/UX Design Bootcamp' },
};

/**
 * Landing v2 hero — DB'de karşılığı olmayan (ya da yetersiz sayıda gerçek
 * kayıt bulunan) kart yuvaları için küratörlü (sunum katmanı) içerik.
 * Kural: uydurma sayısal iddia YOK (ders sayısı/seviye gösterilmez), her
 * kartın `to` alanı GERÇEK ve doğrulanmış bir rotaya gider (kırık link yok).
 * DB'ye hiçbir yeni kurs kaydı EKLENMEZ — bu sadece frontend sunum katmanı.
 */
export const CURATED_CARDS = {
  groundHostess: {
    key: 'ground_hostess',
    category: 'cabin_crew',
    image: '/images/landing/airport-1.webp',
    titleKey: 'landing2.hero.curated.groundHostess.title',
    to: '/courses?category=cabin_crew',
  },
  cabinSim: {
    key: 'cabin_sim',
    category: 'cabin_crew',
    image: '/images/landing/cabin-interior-1.webp',
    titleKey: 'landing2.hero.curated.cabinSim.title',
    to: '/courses?category=cabin_crew',
  },
  aiLiteracy: {
    key: 'ai_literacy',
    category: 'programming',
    image: '/images/landing/ai-literacy-1.webp',
    titleKey: 'landing2.hero.curated.aiLiteracy.title',
    to: '/courses?category=programming',
  },
  // Havuzda beklenen gerçek kurs (kabin memurluğu / İK / en popüler kalan)
  // bulunamazsa kullanılan genel yedek — asla boş kart gösterilmemesi için.
  exploreFallback: {
    key: 'explore_fallback',
    category: null,
    image: '/images/landing/aircraft-1.webp',
    titleKey: 'landing2.hero.curated.exploreFallback.title',
    to: '/courses',
  },
};

/** Ham başlığa göre alias kaydını bulur (tr/en her iki alana da bakar). */
function findCourseAlias(course) {
  if (!course) return null;
  const trRaw = course.title_tr || course.title || '';
  const enRaw = course.title_en || course.title || '';
  return COURSE_TITLE_ALIAS_BY_RAW[trRaw] || COURSE_TITLE_ALIAS_BY_RAW[enRaw] || null;
}

/**
 * getField(course, 'title') sonucunu alias haritasından geçirir. Eşleşme
 * yoksa ham değeri aynen döner — tek kaynak burada, tüm landing bileşenleri
 * (Hero slider, Trending, Popular) bunu kullanır.
 */
export function getDisplayTitle(course, lang, getField) {
  if (!course) return '';
  const alias = findCourseAlias(course);
  if (alias) return lang === 'tr' ? alias.tr : alias.en;
  return getField(course, 'title');
}

/**
 * landing-v3 İş 1: bir kursun DB kategorisi `language` olsa da, sunum
 * katmanında "Havacılık İngilizcesi" olarak gösterilen gerçek kurs mu diye
 * sorar. TrendingCourses bu kursu Havacılık sekmesine taşımak / Diller
 * sekmesinden çıkarmak için kullanır. Kategoriye değil, alias `group`
 * etiketine göre karar verir — tek kaynak `COURSE_TITLE_ALIAS_BY_RAW`.
 */
export function isAviationEnglishCourse(course) {
  const alias = findCourseAlias(course);
  return !!alias && alias.group === 'aviation_english';
}

/**
 * Hero'nun 3 slaydı için her biri TAM 2 kart döndürür (landing-v2-duzeltmeler
 * madde 4 — önceki `buildSlideCourseSets` slayt 1'de <2 gerçek kabin_crew
 * kursu bulunca boş durum gösteriyordu, bu artık MÜMKÜN DEĞİL):
 *
 *  - Slayt 1: kart1 = havuzdaki gerçek `cabin_crew` kursu (Kabin Memurluğu),
 *             kart2 = HER ZAMAN küratörlü "Yer Hostesliği Eğitimi"
 *             (DB'de bu isimde gerçek kurs yok, bilinçli tasarım kararı).
 *  - Slayt 2: kart1 = havuzdaki gerçek `business`/`hr_training` kursu
 *             (İnsan Kaynakları Eğitimi), kart2 = HER ZAMAN küratörlü
 *             "Mock-up Uçak İçi Simülasyon Eğitimi".
 *  - Slayt 3: kart1 = havuzda kalan en popüler gerçek kurs (dinamik),
 *             kart2 = HER ZAMAN küratörlü "Yapay Zekâ Okuryazarlığı".
 *
 * Gerçek kart yuvası (kart1) için havuzda eşleşme yoksa (DB değişir/kurs
 * pasifleşirse) `CURATED_CARDS.exploreFallback` kullanılır — böylece HİÇBİR
 * yuva asla boş kalmaz. Dönen her öğe { type: 'real', course } veya
 * { type: 'curated', card } şeklindedir.
 */
export function buildHeroSlides(pool) {
  const used = new Set();
  const takeOne = (predicate) => {
    for (const c of pool || []) {
      if (used.has(c.course_id)) continue;
      if (predicate && !predicate(c)) continue;
      used.add(c.course_id);
      return c;
    }
    return null;
  };

  const cabinReal = takeOne((c) => c.category === 'cabin_crew');
  const businessReal = takeOne((c) => c.category === 'business' || c.category === 'hr_training');
  const slide3Real = takeOne(null);

  const realOrFallback = (course) =>
    course ? { type: 'real', course } : { type: 'curated', card: CURATED_CARDS.exploreFallback };
  const curated = (card) => ({ type: 'curated', card });

  return [
    [realOrFallback(cabinReal), curated(CURATED_CARDS.groundHostess)],
    [realOrFallback(businessReal), curated(CURATED_CARDS.cabinSim)],
    [realOrFallback(slide3Real), curated(CURATED_CARDS.aiLiteracy)],
  ];
}

/**
 * landing-populer-ve-sayac — "ŞİMDİ POPÜLER" bölümü için kart listesi.
 *
 * `interleaveByCategory` ile havuzdan `limit - 2` (varsayılan 10 → 8) GERÇEK
 * kurs alınır, ardından iki küratörlü kart (bkz. `CURATED_CARDS` — uydurma
 * sayısal iddia YOK, `to` her zaman gerçek bir rotaya gider, DB'ye kayıt
 * EKLENMEZ) sabit index'lere yerleştirilir:
 *   - index 2: `CURATED_CARDS.groundHostess` (Yer Hostesliği Eğitimi)
 *   - index 6: `CURATED_CARDS.aiLiteracy` (Yapay Zekâ Okuryazarlığı)
 * Hero'daki `buildHeroSlides` ile BİREBİR AYNI sözleşme: her öğe
 * `{ type: 'real', course }` ya da `{ type: 'curated', card }` şeklindedir.
 */
export function buildPopularCards(courses, limit = 10) {
  const curatedSlots = [
    [2, CURATED_CARDS.groundHostess],
    [6, CURATED_CARDS.aiLiteracy],
  ].filter(([index]) => index < limit);

  const realCount = Math.max(limit - curatedSlots.length, 0);
  const realCourses = interleaveByCategory(courses, realCount);
  const realCards = realCourses.map((course) => ({ type: 'real', course }));

  const result = [];
  let realIdx = 0;
  for (let i = 0; i < limit; i += 1) {
    const curatedSlot = curatedSlots.find(([index]) => index === i);
    if (curatedSlot) {
      result.push({ type: 'curated', card: curatedSlot[1] });
    } else if (realIdx < realCards.length) {
      result.push(realCards[realIdx]);
      realIdx += 1;
    }
  }

  return result;
}
